import { EnumHelper, ITaskRunner, List, Misc, ModalUtils, Model, NotifyUtils } from "@singularsystems/neo-core";
import { Neo, NeoGrid, Views } from "@singularsystems/neo-react";
import { IReport, ReportingTypes } from "@singularsystems/neo-reporting";
import { observer } from "mobx-react";
import React from "react";
import { NotificationDuration } from "../../../../App/Models/NotificationDuration";
import DividendLetterReport from "../../../../Reporting/Reports/DividendLetter/DividendLetterReport";
import { AppService, DomainTypes, Types } from "../../../DomainTypes";
import EditDividendCommand from "../../../Models/Dividends/Commands/EditDividendCommand";
import { CompoundingType } from "../../../Models/Dividends/CompoundingType.enum";
import DividendAccrualLookup from "../../../Models/Dividends/Lookups/DividendAccrualLookup";
import DividendLookup from "../../../Models/Dividends/Lookups/DividendLookup";
import { IInvestmentHost } from "../../../Models/Investment/IInvestmentHost";
import Investment from "../../../Models/Investment/Investment";
import InvestorInvestment from "../../../Models/InvestorInvestments/InvestorInvestment";
import { InterestRateModifier } from "../../../Models/Rates/InterestRateModifier.enum";
import IssuerDividendLetterReport from "../../../../Reporting/Reports/IssuerDividendLetter/IssuerDividendLetterReport";
import TextValues from "../../../Services/TextValues";

interface IInvestmentDividendsComponentProps {
    taskRunner?: ITaskRunner;
    investmentHost: IInvestmentHost;
}

@observer
export default class InvestmentDividendsComponent extends Views.ViewComponentBase<IInvestmentDividendsComponentProps, InvestmentDividendsComponentVM> {

    constructor(props: IInvestmentDividendsComponentProps) {
        super(props, () => new InvestmentDividendsComponentVM(props.investmentHost, props.taskRunner));

    }

    public render() {
        const investment = this.props.investmentHost.investment,
            interestRateType = investment.calculationOptions.interestRateType,
            hasLinkedRates = interestRateType !== null,
            hasInvestmentRates = interestRateType?.modifierType !== InterestRateModifier.None,
            editingDividend = this.viewModel.editingDividend;

        const requiresLoad = investment.lookup.accruals === null;
        const hasAccruals = requiresLoad || investment.lookup.accruals!.length > 0;
        const hasDividends = investment.lookup.dividends.length > 0;
        const viewByDividend = hasDividends && this.viewModel.viewByDividend;

        return (
            <div>
                <div className="d-flex justify-content-between mb-3">
                    <div>
                        {hasDividends && <Neo.FormGroup bind={this.viewModel.meta.viewByDividend} label={`View By ${TextValues.distribution}`}/>}
                    </div>
                    <div>
                        {this.viewModel.hasLoaded &&
                            <>
                                <Neo.Button
                                    icon="plus"
                                    className="min-width me-1"
                                    onClick={() => this.beginAddDividend()}
                                    tooltip={`Add ${TextValues.distributionLowerCase} date`}>
                                </Neo.Button>
                                <Neo.Button icon="redo"
                                    className="min-width"
                                    variant="light"
                                    tooltip="Re-generate accruals"
                                    onClick={() =>
                                        ModalUtils.showYesNoDismissible("Re-generate", "Are you sure you want to re-generate accruals for this investment?",
                                            () => this.viewModel.fetchAccruals(true))}></Neo.Button>
                            </>}
                    </div>
                </div>

                {viewByDividend &&
                    <NeoGrid.Grid items={this.viewModel.investment.lookup.dividends}>
                        {(dividend, meta) => (
                            <NeoGrid.RowGroup expandProperty={meta.isExpanded} hideExpandButton={dividend.calculations.length === 0}>
                                <NeoGrid.Row>
                                    <NeoGrid.Column display={meta.cycleDate} />
                                    <NeoGrid.Column display={meta.calculationDate} dateProps={{ formatString: "dd MMM yyyy (ddd)" }} />
                                    <NeoGrid.Column display={meta.paidDate} />
                                    <NeoGrid.Column display={meta.calculatedAmount} />
                                    <NeoGrid.Column display={meta.processedAmount}>
                                        {dividend.dividend?.notes ? <div className="cell-note">{dividend.dividend.notes}</div> : undefined}
                                    </NeoGrid.Column>
                                    <NeoGrid.Column display={meta.dividendTax} label={`${TextValues.distribution} Tax`} />
                                    <NeoGrid.Column display={meta.reInvested} />
                                    <NeoGrid.Column display={meta.paidAmount} label="Paid" />
                                    <NeoGrid.ButtonColumn>
                                        {dividend.dividend &&
                                            <Neo.Button icon="edit" size="sm" variant="warning" tooltip="Edit or delete" onClick={() => this.viewModel.editDividend(dividend)} />}
                                        {!dividend.dividend &&
                                            <Neo.Button icon="plus" size="sm" variant="primary" tooltip="Manually process" onClick={() => this.viewModel.editDividend(dividend)} />}
                                    </NeoGrid.ButtonColumn>
                                    <NeoGrid.ButtonColumn>
                                        {!dividend.dividend?.ignore &&
                                            <Neo.Button onClick={() => this.viewModel.viewReport(dividend)} size="sm" variant="secondary" icon="print"></Neo.Button>}
                                    </NeoGrid.ButtonColumn>
                                </NeoGrid.Row>
                                <NeoGrid.ChildRow>
                                    {this.renderAccruals(dividend.calculations, hasLinkedRates, hasInvestmentRates, true)}
                                </NeoGrid.ChildRow>
                            </NeoGrid.RowGroup>
                        )}
                    </NeoGrid.Grid>}
                {!viewByDividend &&
                    <div>
                        {hasAccruals && investment.lookup.accruals && this.renderAccruals(investment.lookup.accruals, hasLinkedRates, hasInvestmentRates, false)}
                        {!hasAccruals &&
                            <Neo.Alert variant="info">
                                Investment does not have any accruals. Please ensure the investment has an initial transaction.
                            </Neo.Alert>}
                    </div>}

                <Neo.Modal
                    title={editingDividend?.dividend?.dividendId ? `Edit ${TextValues.distribution}` : `Process ${TextValues.distribution}`}
                    bindModel={this.viewModel.meta.editingDividend}
                    footerContent={editingDividend?.dividend?.dividendId !== 0 && editingDividend?.canEdit &&
                        <Neo.Button icon="trash" variant="danger" className="min-width" tooltip={`Delete ${TextValues.distributionLowerCase}`} onClick={() => this.beginDeleteDividend(editingDividend)} />}
                    acceptButton={editingDividend?.canEdit && { text: "Save", onClick: () => this.viewModel.saveDividend() }}>
                    {(dividend: EditDividendCommand) => this.renderDividendModal(dividend)}
                </Neo.Modal>
            </div>
        )
    }

    private renderAccruals(accruals: DividendAccrualLookup[], hasLinkedRates: boolean, hasInvestmentRates: boolean, isChild: boolean) {
        return (
            <NeoGrid.Grid items={accruals} style={{ width: "auto" }} filterPredicate={calc => !isChild || !calc.isIgnored} isBordered={isChild}>
                {(calc, calcMeta, isHeader) => (
                    <NeoGrid.Row>
                        <NeoGrid.Column display={calcMeta.startDate} />
                        <NeoGrid.Column display={calcMeta.endDate} label={"End Date\n(excl)"} />
                        <NeoGrid.Column display={calcMeta.noOfDays} />
                        {hasLinkedRates && <NeoGrid.Column display={calcMeta.linkedRate} numProps={{ formatString: "#,##0.00##%" }} />}
                        {hasInvestmentRates && <NeoGrid.Column display={calcMeta.investmentRate} numProps={{ formatString: "#,##0.00##%" }} />}
                        <NeoGrid.Column display={calcMeta.compoundedBalance} label="Capital" />
                        {!isChild &&
                            <>
                                <NeoGrid.Column display={calcMeta.openingAccrued} />
                                <NeoGrid.Column
                                    display={calcMeta.dividendAmount}
                                    style={!isHeader && calc.dividendCycleDateId ? { fontStyle: calc.dividend ? "" : "italic" } : undefined}
                                    suppressDefaultContent={calc.isDividendIgnored}>

                                    {calc.dividendNotes ? <div className="cell-note">{calc.dividendNotes}</div> : undefined}
                                </NeoGrid.Column>
                            </>}
                        <NeoGrid.Column display={calcMeta.accruedForPeriod} />
                        <NeoGrid.Column display={calcMeta.closingAccrued} />
                        {!isChild &&
                            <NeoGrid.ButtonColumn>
                                {calc.dividendLookup && calc.dividendId &&
                                    <Neo.Button icon="edit" size="sm" variant="warning" tooltip="Edit or delete" onClick={() => this.viewModel.editDividend(calc.dividendLookup!)} />}
                                {calc.dividendLookup && !calc.dividendId &&
                                    <Neo.Button icon="plus" size="sm" variant="success" tooltip="Manually process" onClick={() => this.viewModel.editDividend(calc.dividendLookup!)} />}
                            </NeoGrid.ButtonColumn>}
                    </NeoGrid.Row>
                )}
            </NeoGrid.Grid>
        )
    }

    private renderDividendModal(command: EditDividendCommand) {
        const dividend = command.dividend,
            accrual = command.accrual,
            canEdit = command.canEdit;

        return (
            <div>
                {accrual?.isExported && <Neo.Alert variant="danger">{TextValues.distribution} has been exported and cannot be edited.</Neo.Alert>}
                {accrual?.hasLetter && <Neo.Alert variant="danger">A {TextValues.distributionLowerCase} letter has already been authorised for this {TextValues.distributionLowerCase}.</Neo.Alert>}

                <Neo.FormGroupInline display={dividend.meta.cycleDate} dateProps={{ formatString: "ddd dd MMM yyyy" }} />
                {canEdit && command.accrual?.dividendCycleDateId &&
                    <Neo.FormGroupInline bind={dividend.meta.ignore} label="Ignore this cycle date" />}

                {!dividend.ignore &&
                    <>
                        <Neo.FormGroupInline bind={dividend.meta.dividendDate}
                            readOnly={!dividend.isNew}
                            dateProps={{ formatString: "ddd dd MMM yyyy", onDateSelected: () => this.viewModel.onDividendDateChanged() }}
                            footerText={dividend.dividendDateFooter} 
                            label={`${TextValues.distribution} Date`}/>
                        <Neo.FormGroupInline bind={dividend.meta.payDate} readOnly={!canEdit && !command.isLoading} dateProps={{ formatString: "ddd dd MMM yyyy" }} />
                        <Neo.FormGroupInline bind={dividend.meta.dividendAmount} readOnly={!canEdit && !command.isLoading} label={`${TextValues.distribution} Amount`} />
                        <Neo.FormGroupInline bind={dividend.meta.dividendTax} readOnly={!canEdit} label={`${TextValues.distribution} Tax`} />
                        <Neo.FormGroupInline>
                            <Neo.AutoCompleteDropDown bind={dividend.meta.compoundingType} readOnly={!canEdit} items={EnumHelper.asList(CompoundingType)} placeholder="Use investment default" />
                        </Neo.FormGroupInline>
                        <Neo.FormGroupInline
                            bind={dividend.meta.reinvestAmount}
                            readOnly={!command.canEditReinvestAmount}
                            label="Reinvest Amount"
                            onChange={() => dividend.paidAmount = dividend.dividendAmount - dividend.reinvestAmount}
                        />
                        <Neo.FormGroupInline display={dividend.meta.paidAmount} />
                        <Neo.FormGroupInline display={command.meta.valuePerShare} label="Value Per Share" />
                        <Neo.FormGroupInline display={command.meta.noOfShares} label="No. of Shares" />
                    </>}
            </div>
        )
    }

    private async beginAddDividend() {
        const dateProperty = Model.ObservableProperty.required(`${TextValues.distribution} Date`, null, { propertyType: Misc.DataType.Date });

        const result = await ModalUtils.showOkCancel(`Add ${TextValues.distribution}`, <Neo.FormGroupInline bind={dateProperty} />, dateProperty);
        if (result === Misc.ModalResult.Yes) {
            this.viewModel.addDividend(dateProperty.value);
        }
    }

    private beginDeleteDividend(command: EditDividendCommand) {
        ModalUtils.showYesNoDismissible(`Delete ${TextValues.distribution}`, `Are you sure you want to delete this ${TextValues.distribution}?`, () => this.viewModel.deleteDividend(command.dividend.dividendId));
    }
}

class InvestmentDividendsComponentVM extends Views.ViewModelBase {

    constructor(
        public investmentHost: IInvestmentHost,
        taskRunner: ITaskRunner = AppService.get(Types.Neo.TaskRunner),
        private investmentApiClient = AppService.get(DomainTypes.ApiClients.Investment),
        private reportingService = AppService.get(ReportingTypes.Services.ReportingService),
        private dataCache = AppService.get(DomainTypes.Services.DataCache)) {
        super(taskRunner);

        this.makeObservable();

        this.investment = investmentHost.investment;
        this.isInvestor = this.investmentHost instanceof InvestorInvestment
    }

    public investment: Investment;
    public isInvestor: boolean = false;
    public viewByDividend = true;
    public hasLoaded = false;

    public editingDividend: EditDividendCommand | null = null;

    public async initialise() {
        if (this.investment.lookup.accruals === null) {
            await this.fetchAccruals();
        }
        this.hasLoaded = true;
    }

    public async fetchAccruals(regenerate: boolean = false) {
        const data = await this.taskRunner.waitForData(this.investmentApiClient.getDividendAccruals(this.investment.investmentId, regenerate));
        this.investment.lookup.setAccruals(List.fromJSArray(DividendAccrualLookup, data).sortBy("startDate"));
    }

    public async viewReport(dividend: DividendLookup) {
        let report: IReport;

        if (this.isInvestor) {
            report = await this.taskRunner.waitFor(
                this.reportingService.createReport(
                    DividendLetterReport,
                    { dividendAccrualId: dividend.accrual.dividendAccrualId, dividendDate: dividend.calculationDate, entityId: this.investmentHost.entityId })) as IReport;
        } else {
            report = await this.taskRunner.waitFor(
                this.reportingService.createReport(
                    IssuerDividendLetterReport,
                    { dividendAccrualId: dividend.accrual.dividendAccrualId, dividendDate: dividend.calculationDate, entityId: this.investmentHost.entityId })) as IReport;  
        }
        
        this.reportingService.viewReportModal(report as IReport);
    }

    public addDividend(dividendDate: Date) {
        if (this.investment.lookup.accruals) {
            const accrual = this.investment.lookup.findAccrual(dividendDate);
            if (accrual) {
                if (accrual.dividendLookup && accrual.startDate.getTime() === dividendDate.getTime()) {
                    this.editDividend(accrual.dividendLookup);
                } else {
                    this.editDividend(EditDividendCommand.newDividend(dividendDate));
                    this.onDividendDateChanged();
                }
            } else {
                ModalUtils.showMessage(`Add ${TextValues.distribution}`, `${TextValues.distribution} date must be within the investment period.`);
            }
        }
    }

    public async editDividend(dividend: DividendLookup | EditDividendCommand) {
        let command: EditDividendCommand;
        if (dividend instanceof DividendLookup) {
            command = EditDividendCommand.fromDividendLookup(dividend);
            if (!command.dividend.payDate) {
                command.dividend.payDate = await this.taskRunner.waitFor(this.dataCache.calculateBusinessDayAsync(dividend.calculationDate, this.investment.calculationOptions.paymentDateConvention, this.investment.calculationOptions.paymentDateOffset));
            }
        } else {
            command = dividend;
        }

        command.valuePerShare = this.investment.valuePerShare;
        command.dividend.valuePerShare = this.investment.valuePerShare;
        command.isInvestor = this.isInvestor;
        command.shareTransactions = Array.from(this.investment.lookup.shareTransactions);
        this.editingDividend = command;
    }

    public async onDividendDateChanged() {
        if (this.editingDividend && this.editingDividend.dividend.dividendDate) {
            const dividend = this.editingDividend.dividend,
                dividendDate = dividend.dividendDate!;

            this.editingDividend.isLoading = true;

            const accrual = this.investment.lookup!.findAccrual(dividendDate);
            dividend.dividendAmount = accrual?.getAccrualAtDate(dividendDate, dividend.cycleDate === null) ?? 0;

            dividend.payDate = await this.taskRunner.waitFor(this.dataCache.calculateBusinessDayAsync(dividendDate, this.investment.calculationOptions.paymentDateConvention, this.investment.calculationOptions.paymentDateOffset));

            this.editingDividend.isLoading = false;
        }
    }

    public deleteDividend(dividendId: number) {
        return this.taskRunner.run(async () => {

            await this.investmentApiClient.deleteDividend(dividendId);
            this.editingDividend = null;

            NotifyUtils.addSuccess(`${TextValues.distribution} deleted successfully.`, "", NotificationDuration.Standard);

            await this.fetchAccruals();
        });
    }

    public saveDividend() {
        return this.taskRunner.run(async () => {

            if (this.editingDividend) {
                this.editingDividend.beforeSave(this.investment.investmentId);

                await this.investmentApiClient.editDividend(this.editingDividend.toJSObject());
                this.editingDividend = null;

                NotifyUtils.addSuccess(`${TextValues.distribution} saved successfully.`, "", NotificationDuration.Standard);

                await this.fetchAccruals();
            }
        });
    }
}